import React from 'react'
import { useFormFactor, useGroup } from '../../../hooks'
import { MtLogo } from './../../logos'
import { BookmarkButton, ActionButton } from '../../action-buttons'
import {
  CollectionsBookmarkTwoTone,
  ThumbUpTwoTone,
  ThumbUp,
  Bookmark,
  BookmarkBorder
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { contentUrl } from '../../../helpers/url'
import { ALLOW_LIKE_GROUP, ALLOW_PLAYLISTS_GROUP } from '../../user-group-policy/permissions'
import { UserGroupPolicy } from '../../user-group-policy'
import { ALL_CONSUMER } from '../../../services/cognito/userGroups'
import { useStyles } from './styles'
import { useDisplayAddToPlaylist } from '../../../hooks/useDisplayAddToPlaylist'
import { useLike } from '../../like'
import { useAddToBookmarks } from '../../add-to-bookmarks'
import { Box } from '@mui/material'

export const PROMOTED_CARD_TYPES = {
  MAIN: 'MAIN',
  SECONDARY: 'SECONDARY',
  GENERIC: 'GENERIC'
}

const usePromotedCardMethods = ({
  content,
  size,
  position,
  cardType,
  onSelect,
  onAddToPlaylist
}) => {
  const { isDesktop } = useFormFactor()

  const cardSize = size || (isDesktop ? 'large' : 'mobile')
  const { isInGroup } = useGroup()
  const isConsumer = isInGroup(ALL_CONSUMER)
  const classes = useStyles({ isConsumer })
  const displayAddToPlaylist = useDisplayAddToPlaylist()

  const { isBookmarked, handleBookmark, bookmarking } = useAddToBookmarks({ from: 'promoted-content' })
  const { handleLike: onLike, liking, isLiked } = useLike({ from: 'promoted-content' })
  const hasRecommend = isLiked(content || {})
  const isLiking = liking(content || {})

  const onBookmark = () => {
    if (!content) return
    handleBookmark(content)
  }

  const isBookmarking = bookmarking(content || {})
  const bookmarked = isBookmarked(content || {})

  let contentImage = MtLogo

  if (content?.image?.imgUrl) {
    const dimensions = size === 'large' ? 'w=540&h=380' : 'w=367&h=202'
    contentImage = content?.image?.imgUrl + `fit=crop&${dimensions}`
  }

  const navigate = useNavigate()
  const handleCardSelect = () => {
    if (!content) return
    if (onSelect) {
      onSelect(content, position)
    }
    navigate(contentUrl(content, 'promoted', position))
  }

  const handleLike = () => onLike(content)

  const handleAddToPlaylist = () => {
    onAddToPlaylist(content)
  }

  const actions = {
    leftActions: (
      <Box display='flex' flexDirection='row' alignItems='center' mt={1} gap={1}>
        <UserGroupPolicy
          accessGroup={ALLOW_LIKE_GROUP}
          componentAllow={
            <ActionButton
              size='xs'
              variant={hasRecommend ? 'secondary' : 'secondary-noselect'}
              label={hasRecommend ? 'Liked' : 'Like'}
              startIcon={hasRecommend ? <ThumbUp /> : <ThumbUpTwoTone />}
              onClick={handleLike}
              loading={isLiking}
              hideText={cardType !== PROMOTED_CARD_TYPES.MAIN}
              className={classes.action}
            />
          }
        />
        <BookmarkButton
          bookmarked={bookmarked}
          variant={bookmarked ? 'secondary' : 'secondary-noselect'}
          size='xs'
          hideText={cardType !== PROMOTED_CARD_TYPES.MAIN}
          loading={isBookmarking}
          onClick={onBookmark}
          startIcon={bookmarked ? <Bookmark /> : <BookmarkBorder />}
          className={classes.action}
        />
        {displayAddToPlaylist && (
          <UserGroupPolicy
            accessGroup={ALLOW_PLAYLISTS_GROUP}
            componentAllow={
              <ActionButton
                variant='secondary-noselect'
                size='xs'
                label='Add to Playlist'
                startIcon={<CollectionsBookmarkTwoTone />}
                onClick={handleAddToPlaylist}
                className={classes.action}
              />
            }
          />
        )}
      </Box>
    )
  }

  return {
    contentImage,
    handleCardSelect,
    cardSize,
    actions
  }
}

export default usePromotedCardMethods
